<template>
  <div
    :class="[
      'flex text-base',
      compactSearchBar
        ? 'mt-0 ml-0 min-w-0 w-full items-center justify-start gap-4'
        : 'mt-2 items-center space-x-5',
    ]">
    <div
      v-if="!compactSearchBar"
      class="shrink-0 text-base font-semibold">
      {{ Name }}
    </div>
    <div
      class="pickstore-vs-shell overflow-hidden rounded-md border border-gray-800 bg-white text-sm text-nowrap"
      :class="
        compactSearchBar
          ? 'h-8 min-h-8 w-full max-w-full min-w-0'
          : 'h-8 w-64 shrink-0 min-w-0'
      ">
      <v-select
        v-model="selectedSupplier"
        :options="optionList"
        label="strSupplierName"
        :placeholder="defaultNm"
        :reduce="(store) => (store != null ? store.lngSupplierID : null)"
        :clearable="!disable"
        append-to-body
        :disabled="disable"
        class="style-chooser pickstore-chooser h-full !disabled:text-black text-sm">
        <template #no-options>
          <div>검색된 항목이 없습니다.</div>
        </template>
        <template #no-results>
          <div>검색된 항목이 없습니다.</div>
        </template>
      </v-select>
    </div>
  </div>
</template>

<script setup>
import { getSuppliers } from "@/api/master";
import store from "@/store";
import { onMounted, ref, watch } from "vue";

const optionList = ref([]);
const selectedSupplier = ref("");
const selectedSupplierNm = ref("");
const emit = defineEmits(["SupplierId", "SupplierNm"]);
const props = defineProps({
  disable: {
    type: Boolean,
    default: false,
  },
  selectSupplierId: {
    type: [String, Number],
    default: "",
  },
  /** `selectSupplierId`가 목록에 없을 때(거래처 전용 계정 등) 표시할 거래처명 */
  selectSupplierLabel: {
    type: String,
    default: "",
  },
  /** businessClient.vue `defaultNm` 과 동일 — 미선택 시 표시·emit 문구 (조회에서 `전체`가 필요하면 `:default-nm="'전체'"`) */
  defaultNm: {
    type: String,
    default: "선택",
  },
  defaultName: {
    type: String,
    default: "거래처",
  },
  /** PickStore `compact-search-bar`와 동일 — 조회줄에서 레이아웃·콤보 셸 통일 */
  compactSearchBar: {
    type: Boolean,
    default: false,
  },
});

const disableBusiness = ref(false);
const Name = ref("");
watch(
  () => props.disable,
  () => {
    disableBusiness.value = props.disable;
  }
);
watch(
  () => props.selectSupplierId,
  () => {
    ensurePresetOptionInList(props.selectSupplierId);
    applyPresetSupplier(props.selectSupplierId);
  }
);

watch(
  () => props.selectSupplierLabel,
  () => {
    ensurePresetOptionInList(props.selectSupplierId);
    applyPresetSupplier(props.selectSupplierId);
  }
);

function hasPresetId(id) {
  return (
    id !== "" &&
    id != null &&
    id !== 0 &&
    id !== "0"
  );
}

/** 프리셋 ID가 getSuppliers 목록에 없으면 행을 넣어 v-select가 이름(label)로 표시하도록 함 */
function ensurePresetOptionInList(presetId) {
  if (!hasPresetId(presetId)) return;
  const idStr = String(presetId);
  const idx = optionList.value.findIndex(
    (i) => String(i.lngSupplierID) === idStr
  );
  const labelFromProp =
    props.selectSupplierLabel && String(props.selectSupplierLabel).trim();
  const label = labelFromProp || `거래처 (${idStr})`;
  const nid = Number(presetId);
  const idVal = Number.isFinite(nid) ? nid : presetId;

  if (idx >= 0) {
    if (
      labelFromProp &&
      optionList.value[idx].strSupplierName !== labelFromProp
    ) {
      const copy = [...optionList.value];
      copy[idx] = { ...copy[idx], strSupplierName: labelFromProp };
      optionList.value = copy;
    }
    return;
  }
  optionList.value = [
    { lngSupplierID: idVal, strSupplierName: label },
    ...optionList.value,
  ];
}

function applyPresetSupplier(presetId) {
  if (hasPresetId(presetId)) {
    const found = optionList.value.find(
      (i) => String(i.lngSupplierID) === String(presetId)
    );
    if (found) {
      selectedSupplier.value = found.lngSupplierID;
      selectedSupplierNm.value = found.strSupplierName ?? "";
    } else {
      selectedSupplier.value = Number.isFinite(Number(presetId))
        ? Number(presetId)
        : presetId;
      selectedSupplierNm.value = "";
    }
  } else {
    selectedSupplier.value = "";
    selectedSupplierNm.value = props.defaultNm;
  }
}

onMounted(async () => {
  try {
    const gid = store.state.userData?.lngStoreGroup;
    const res = await getSuppliers(gid);
    optionList.value = Array.isArray(res?.data?.List) ? res.data.List : [];
  } catch {
    optionList.value = [];
  }

  Name.value = props.defaultName;

  const presetId = props.selectSupplierId;
  ensurePresetOptionInList(presetId);
  applyPresetSupplier(presetId);

  emit("SupplierId", selectedSupplier.value);
  emit("SupplierNm", selectedSupplierNm.value);
});

watch(selectedSupplier, (newVal) => {
  if (newVal === null || newVal === undefined || newVal === "") {
    selectedSupplierNm.value = props.defaultNm;
    emit("SupplierId", "");
    emit("SupplierNm", props.defaultNm);
    return;
  }
  const found = optionList.value.find(
    (i) => String(i.lngSupplierID) === String(newVal)
  );
  selectedSupplierNm.value = found ? found.strSupplierName : "";
  emit("SupplierId", selectedSupplier.value);
  emit("SupplierNm", selectedSupplierNm.value);
});
</script>

<!-- pickStore와 분리 번들에서도 동일 셸 스타일 적용 -->
<style>
.pickstore-vs-shell .style-chooser .vs__dropdown-toggle {
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}
</style>
